package br.com.heyagenda.presentation.booking

import br.com.heyagenda.data.remote.AppointmentDto
import br.com.heyagenda.data.remote.FirestoreAppointmentDataSource
import br.com.heyagenda.data.remote.FirestoreAvailabilityDataSource
import br.com.heyagenda.data.remote.FirestoreClientDataSource
import br.com.heyagenda.data.remote.FirestoreProfessionalDataSource
import br.com.heyagenda.data.remote.FirestoreServiceDataSource
import br.com.heyagenda.di.AppModule
import br.com.heyagenda.domain.model.Appointment
import br.com.heyagenda.domain.model.AppointmentStatus
import br.com.heyagenda.domain.model.CalendarUtils
import br.com.heyagenda.domain.model.ProfessionalAvailability
import br.com.heyagenda.domain.model.SavedClient
import br.com.heyagenda.domain.model.StudioService
import br.com.heyagenda.domain.usecase.GetProfessionalBySlugUseCase
import br.com.heyagenda.domain.util.DateTimeZoneUtils
import br.com.heyagenda.domain.util.ErrorSanitizer
import br.com.heyagenda.domain.util.getPlatformCurrentDateTime
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.supervisorScope

class PublicBookingScreenModel(
    private val slugOrUid: String,
    private val getProfessionalBySlugUseCase: GetProfessionalBySlugUseCase,
    private val firestoreServiceDataSource: FirestoreServiceDataSource,
    private val firestoreAvailabilityDataSource: FirestoreAvailabilityDataSource,
    private val firestoreAppointmentDataSource: FirestoreAppointmentDataSource,
    private val firestoreProfessionalDataSource: FirestoreProfessionalDataSource = AppModule.firestoreProfessionalDataSource,
    private val firestoreClientDataSource: FirestoreClientDataSource = AppModule.firestoreClientDataSource
) : ScreenModel {

    private val _uiState = MutableStateFlow(PublicBookingUiState(isLoading = true))
    val uiState: StateFlow<PublicBookingUiState> = _uiState.asStateFlow()

    private var currentAvailability: ProfessionalAvailability? = null

    init {
        loadProfessionalData()
    }

    private fun loadProfessionalData() {
        screenModelScope.launch {
            if (slugOrUid.isBlank()) {
                _uiState.update { it.copy(isLoading = false, notFound = true) }
                return@launch
            }

            val pro = getProfessionalBySlugUseCase(slugOrUid)
            if (pro == null) {
                _uiState.update { it.copy(isLoading = false, isLoadingServices = false, notFound = true) }
                return@launch
            }

            val (uid, profile) = pro
            val studio = profile.studioName?.takeIf { it.isNotBlank() } ?: profile.name.ifBlank { "Estúdio" }
            val name = profile.name.ifBlank { "Profissional" }
            val specialty = profile.specialty.orEmpty()
            val isPro = profile.subscriptionTier?.equals("pro", ignoreCase = true) == true

            _uiState.update {
                it.copy(
                    isLoading = false,
                    notFound = false,
                    resolvedUid = uid,
                    studioName = studio,
                    professionalName = name,
                    specialty = specialty,
                    bio = "Agendamento online oficial de $studio. Escolha o serviço desejado abaixo.",
                    themeAccentColor = profile.themeAccentColor,
                    instagramHandle = profile.instagramHandle,
                    address = profile.address,
                    announcementBanner = profile.announcementBanner,
                    isPro = isPro,
                    professionalPhone = profile.phone
                )
            }

            // Record page view anonymously for analytics (deduplicated per visitor within 24h window)
            launch {
                try {
                    if (br.com.heyagenda.domain.util.PlatformVisitTracker.shouldRecordVisit(uid, 24)) {
                        val todayIso = CalendarUtils.getTodayIso()
                        firestoreProfessionalDataSource.recordPageView(uid, todayIso)
                    }
                } catch (_: Throwable) {}
            }

            val candidateUids = listOfNotNull(
                uid.takeIf { it.isNotBlank() },
                slugOrUid.takeIf { it.isNotBlank() },
                br.com.heyagenda.domain.util.SlugUtils.toSlug(slugOrUid).takeIf { it.isNotBlank() }
            ).distinct()

            supervisorScope {
                // 1. Direct get and observe services
                launch {
                    try {
                        var foundDirect = false
                        // Immediate direct fetch across candidate UIDs for instant loading
                        for (targetUid in candidateUids) {
                            val directList = firestoreServiceDataSource.getServices(targetUid)
                            if (directList.isNotEmpty()) {
                                println(">>> [PublicBooking] Found ${directList.size} services on direct get for $targetUid")
                                val domainServices = directList.map { dto ->
                                    StudioService(
                                        id = dto.id,
                                        name = dto.name,
                                        category = dto.category,
                                        priceFormatted = dto.priceFormatted,
                                        durationMinutes = dto.durationMinutes,
                                        description = dto.description
                                    )
                                }
                                _uiState.update { it.copy(services = domainServices, isLoadingServices = false) }
                                foundDirect = true
                                break
                            }
                        }

                        // Stream real-time updates safely
                        firestoreServiceDataSource.observeServices(uid)
                            .catch { e ->
                                println(">>> [PublicBooking] observeServices error: ${e.message}")
                                _uiState.update { it.copy(isLoadingServices = false) }
                            }
                            .collect { dtos ->
                                val domainServices = dtos.map {
                                    StudioService(
                                        id = it.id,
                                        name = it.name,
                                        category = it.category,
                                        priceFormatted = it.priceFormatted,
                                        durationMinutes = it.durationMinutes,
                                        description = it.description
                                    )
                                }
                                _uiState.update { it.copy(services = domainServices, isLoadingServices = false) }
                            }
                    } catch (e: Throwable) {
                        println(">>> [PublicBooking] Services coroutine error: ${e.message}")
                        _uiState.update { it.copy(isLoadingServices = false) }
                    }
                }

                // 2. Direct get and observe real availability
                launch {
                    try {
                        for (targetUid in candidateUids) {
                            val directAvail = firestoreAvailabilityDataSource.getAvailability(targetUid)
                            if (directAvail != null) {
                                currentAvailability = directAvail
                                recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
                                break
                            }
                        }

                        firestoreAvailabilityDataSource.observeAvailability(uid)
                            .catch { e -> println(">>> [PublicBooking] observeAvailability error: ${e.message}") }
                            .collect { avail ->
                                if (avail != null) {
                                    currentAvailability = avail
                                    recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
                                }
                            }
                    } catch (e: Throwable) {
                        println(">>> [PublicBooking] Availability coroutine error: ${e.message}")
                    }
                }

                // 3. Direct get and observe existing appointments within booking window (TAKEN SLOTS)
                launch {
                    try {
                        val todayIso = br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()
                        val startOfMonth = br.com.heyagenda.domain.model.CalendarUtils.getStartOfMonthIso()
                        val endRangeIso = br.com.heyagenda.domain.model.CalendarUtils.getNextDays(45).lastOrNull()?.dateIso ?: todayIso
                        val queryStartIso = if (startOfMonth < todayIso) startOfMonth else todayIso
                        val currentMonthPrefix = todayIso.take(7)

                        fun processAppointments(appts: List<AppointmentDto>) {
                            val activeMonthlyCount = appts.count { appt ->
                                val status = appt.status.trim().uppercase()
                                status != "CANCELLED" && status != "CANCELED" && appt.date.startsWith(currentMonthPrefix)
                            }
                            val limitReached = !_uiState.value.isPro && activeMonthlyCount >= 60
                            _uiState.update {
                                it.copy(
                                    existingAppointments = appts,
                                    monthlyAppointmentsCount = activeMonthlyCount,
                                    hasReachedMonthlyLimit = limitReached
                                )
                            }
                            recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
                        }

                        for (targetUid in candidateUids) {
                            val directAppts = firestoreAppointmentDataSource.getAppointmentsInRange(targetUid, queryStartIso, endRangeIso)
                            if (directAppts.isNotEmpty()) {
                                processAppointments(directAppts)
                                break
                            }
                        }

                        firestoreAppointmentDataSource.observeAppointmentsInRange(uid, queryStartIso, endRangeIso)
                            .catch { e ->
                                println(">>> [PublicBooking] Appointments observe notice: ${e.message}")
                            }
                            .collect { appts ->
                                if (appts.isNotEmpty() || _uiState.value.existingAppointments.isEmpty()) {
                                    processAppointments(appts)
                                }
                            }
                    } catch (e: Throwable) {
                        println(">>> [PublicBooking] Appointments coroutine error: ${e.message}")
                    }
                }
            }
        }
    }

    fun toggleService(service: StudioService) {
        val current = _uiState.value.selectedServices.toMutableSet()
        if (current.contains(service)) {
            current.remove(service)
        } else {
            current.add(service)
        }
        _uiState.update { it.copy(selectedServices = current) }
        recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
    }

    fun selectServiceAndOpen(service: StudioService) {
        val current = _uiState.value.selectedServices.toMutableSet()
        current.add(service)
        _uiState.update {
            it.copy(
                selectedServices = current,
                isBookingModalOpen = true
            )
        }
        recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
    }

    fun removeService(service: StudioService) {
        val current = _uiState.value.selectedServices.toMutableSet()
        current.remove(service)
        val shouldStayOpen = current.isNotEmpty()
        _uiState.update {
            it.copy(
                selectedServices = current,
                isBookingModalOpen = if (shouldStayOpen) it.isBookingModalOpen else false
            )
        }
        recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
    }

    fun refreshAppointments() {
        val uid = _uiState.value.resolvedUid ?: return
        screenModelScope.launch {
            try {
                val todayIso = br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()
                val endRangeIso = br.com.heyagenda.domain.model.CalendarUtils.getNextDays(45).lastOrNull()?.dateIso ?: todayIso
                val direct = firestoreAppointmentDataSource.getAppointmentsInRange(uid, todayIso, endRangeIso)
                if (direct.isNotEmpty()) {
                    _uiState.update { it.copy(existingAppointments = direct) }
                    recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
                }
            } catch (e: Throwable) {
                println(">>> [PublicBooking] refreshAppointments error: ${e.message}")
            }
        }
    }

    fun openBookingModal() {
        if (_uiState.value.selectedServices.isEmpty()) return
        _uiState.update { it.copy(isBookingModalOpen = true) }
        recalculateAvailableSlots(_uiState.value.totalDurationMinutes)
        refreshAppointments()
    }

    fun selectService(service: StudioService) {
        selectServiceAndOpen(service)
    }

    fun selectTimeSlot(slot: String) {
        _uiState.update { it.copy(selectedTimeSlot = slot) }
    }

    fun selectDate(dateIso: String, dateText: String) {
        _uiState.update { it.copy(selectedDateIso = dateIso, selectedDateText = dateText) }
        val duration = _uiState.value.totalDurationMinutes
        recalculateAvailableSlots(duration)
        refreshAppointments()
    }

    fun updateClientName(name: String) {
        _uiState.update { it.copy(clientName = name) }
    }

    fun updateClientPhone(phone: String) {
        val digits = phone.filter { it.isDigit() }.take(11)
        _uiState.update { it.copy(clientPhone = digits) }
    }

    fun updateClientNotes(notes: String) {
        _uiState.update { it.copy(clientNotes = notes) }
    }

    fun updatePaymentMethod(method: String) {
        _uiState.update { it.copy(paymentMethod = method) }
    }

    fun closeModal() {
        _uiState.update { it.copy(isBookingModalOpen = false) }
    }

    fun submitBooking() {
        val state = _uiState.value
        if (state.hasReachedMonthlyLimit) {
            _uiState.update {
                it.copy(
                    isSubmitting = false,
                    submissionErrorMessage = "Agendamentos online pausados para este mês."
                )
            }
            return
        }
        if (state.selectedServices.isEmpty()) return
        val uid = state.resolvedUid ?: return
        val slot = state.selectedTimeSlot ?: state.availableTimeSlots.firstOrNull() ?: return

        if (state.clientName.isBlank()) return

        screenModelScope.launch {
            _uiState.update { it.copy(isSubmitting = true) }

            val todayIso = br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()
            val appointmentDate = state.selectedDateIso.ifBlank { todayIso }

            val totalDuration = state.totalDurationMinutes
            val endTime = computeEndTime(slot, totalDuration)
            val combinedServicesName = state.selectedServiceNames
            val combinedPrice = state.totalPriceFormatted

            val currentDt = getPlatformCurrentDateTime()
            val startUtc = DateTimeZoneUtils.localToUtcIso(appointmentDate, slot, currentDt.utcOffsetMinutes)
            val endUtc = DateTimeZoneUtils.localToUtcIso(appointmentDate, endTime, currentDt.utcOffsetMinutes)

            val notesText = if (state.clientNotes.isNotBlank()) {
                "${state.clientNotes.trim()} (Data: ${state.selectedDateText}, Agendado online)"
            } else {
                "Data: ${state.selectedDateText} | Agendado via Link Web Público"
            }

            val digits = state.clientPhone.filter { it.isDigit() }.take(11)
            val formattedPhone = if (digits.length >= 10) {
                br.com.heyagenda.domain.util.PhoneUtils.formatDigits(digits)
            } else {
                digits.ifBlank { null }
            }

            val appointmentDto = AppointmentDto(
                id = "",
                clientName = state.clientName.trim(),
                clientPhone = formattedPhone,
                serviceName = combinedServicesName,
                date = appointmentDate,
                startTime = slot,
                endTime = endTime,
                priceFormatted = combinedPrice,
                status = "CONFIRMED",
                paymentMethod = state.paymentMethod,
                notes = notesText,
                startUtcIso = startUtc,
                endUtcIso = endUtc
            )

            val result = firestoreAppointmentDataSource.saveAppointment(uid, appointmentDto)
            if (result.isSuccess) {
                // Sincroniza / salva automaticamente o cliente em "Meus Clientes"
                try {
                    val clientPhone = appointmentDto.clientPhone?.takeIf { it.isNotBlank() }
                    firestoreClientDataSource.saveClient(
                        uid,
                        SavedClient(
                            id = "",
                            name = appointmentDto.clientName,
                            phone = clientPhone,
                            totalAppointments = 1,
                            lastAppointmentDate = appointmentDto.date,
                            lastServiceName = appointmentDto.serviceName
                        )
                    )
                } catch (e: Throwable) {
                    println(">>> [PublicBooking] Error saving client: ${e.message}")
                }

                val confirmed = Appointment(
                    id = "",
                    clientName = state.clientName.trim(),
                    clientPhone = state.clientPhone.trim().ifBlank { null },
                    serviceName = combinedServicesName,
                    date = appointmentDate,
                    startTime = slot,
                    endTime = endTime,
                    priceFormatted = combinedPrice,
                    status = AppointmentStatus.CONFIRMED,
                    paymentMethod = state.paymentMethod,
                    notes = notesText,
                    startUtcIso = startUtc,
                    endUtcIso = endUtc
                )
                val updatedAppts = _uiState.value.existingAppointments + appointmentDto
                _uiState.update {
                    it.copy(
                        isSubmitting = false,
                        isBookingModalOpen = false,
                        confirmedAppointment = confirmed,
                        existingAppointments = updatedAppts,
                        submissionErrorMessage = null
                    )
                }
                recalculateAvailableSlots(state.totalDurationMinutes)
            } else {
                _uiState.update {
                    it.copy(
                        isSubmitting = false,
                        submissionErrorMessage = ErrorSanitizer.sanitize(
                            result.exceptionOrNull(),
                            "Não foi possível concluir o agendamento no momento. Tente novamente."
                        )
                    )
                }
            }
        }
    }

    fun resetBooking() {
        _uiState.update {
            it.copy(
                confirmedAppointment = null,
                selectedServices = emptySet(),
                isBookingModalOpen = false,
                selectedTimeSlot = null,
                clientNotes = ""
            )
        }
        recalculateAvailableSlots(0)
        refreshAppointments()
    }

    private fun recalculateAvailableSlots(durationMinutes: Int) {
        val slots = generateSlots(durationMinutes)
        _uiState.update {
            val currentSelected = it.selectedTimeSlot
            val newSelected = if (currentSelected != null && slots.contains(currentSelected)) {
                currentSelected
            } else {
                slots.firstOrNull()
            }
            it.copy(
                availableTimeSlots = slots,
                selectedTimeSlot = newSelected
            )
        }
    }

    private fun normalizeDateIso(raw: String): String {
        if (raw.isBlank()) return ""
        return try {
            val parts = raw.trim().split("-")
            if (parts.size == 3) {
                val y = parts[0].toInt()
                val m = parts[1].toInt()
                val d = parts[2].toInt()
                "$y-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
            } else {
                raw.trim()
            }
        } catch (_: Throwable) {
            raw.trim()
        }
    }

    private fun parseTimeToMinutes(timeStr: String?, defaultMin: Int): Int {
        if (timeStr.isNullOrBlank()) return defaultMin
        return try {
            val clean = timeStr.trim()
            val isPm = clean.endsWith("PM", ignoreCase = true)
            val isAm = clean.endsWith("AM", ignoreCase = true)
            val timePart = clean.replace("(?i)[AP]M".toRegex(), "").trim()
            val parts = timePart.split(":")
            var h = parts[0].toInt()
            val m = if (parts.size > 1) parts[1].toInt() else 0
            if (isPm && h < 12) h += 12
            if (isAm && h == 12) h = 0
            h * 60 + m
        } catch (_: Throwable) {
            defaultMin
        }
    }

    private data class WorkingHoursConfig(
        val isOpen: Boolean,
        val startMin: Int,
        val endMin: Int,
        val breakStartMin: Int?,
        val breakEndMin: Int?
    )

    private fun generateSlots(durationMinutes: Int): List<String> {
        val step = if (durationMinutes > 0) durationMinutes else 30
        val currentDt = getPlatformCurrentDateTime()
        val todayIso = currentDt.dateIso
        val selectedDateStr = _uiState.value.selectedDateIso.ifBlank { todayIso }

        val avail = currentAvailability ?: ProfessionalAvailability()

        // 1. Check Days Off (Folgas, Férias, Feriados)
        val isDayOff = avail.daysOff.any { off ->
            off.dateIso == selectedDateStr ||
            (off.isRecurringYearly && off.dateIso.length >= 5 && selectedDateStr.endsWith(off.dateIso.takeLast(5)))
        }
        if (isDayOff) {
            println(">>> [SlotGen] Date $selectedDateStr is marked as DAY OFF for the professional")
            return emptyList()
        }

        // 2. Check Date Specific Overrides or Weekly Schedule
        val dateOverride = avail.dateOverrides.firstOrNull { it.dateIso == selectedDateStr }
        val intervals = if (dateOverride != null) {
            if (!dateOverride.isOpen) {
                println(">>> [SlotGen] Date $selectedDateStr is marked as CLOSED by date override")
                return emptyList()
            }
            dateOverride.effectiveIntervals
        } else {
            val dayOfWeekName = CalendarUtils.getDayOfWeekForIso(selectedDateStr)
            val daySchedule = avail.weeklySchedule.firstOrNull { it.dayOfWeek == dayOfWeekName }
            if (daySchedule == null || !daySchedule.isOpen) {
                println(">>> [SlotGen] Day $dayOfWeekName ($selectedDateStr) is marked as CLOSED in weekly schedule")
                return emptyList()
            }
            daySchedule.effectiveIntervals
        }

        if (intervals.isEmpty()) {
            return emptyList()
        }

        // 3. Past time threshold if date is TODAY
        val isToday = selectedDateStr == todayIso || _uiState.value.selectedDateIso.isBlank() || _uiState.value.selectedDateText == "Hoje"
        val nowThresholdMinutes = if (isToday) currentDt.totalMinutes else -1

        // 4. Appointments already booked (TAKEN SLOTS) with UTC interval mapping
        val activeAppointmentsWithUtc = _uiState.value.existingAppointments.filter { appt ->
            val status = appt.status.trim().uppercase()
            status != "CANCELLED" && status != "CANCELED"
        }.mapNotNull { appt ->
            val startUtc = if (appt.startUtcIso.isNotBlank()) {
                appt.startUtcIso
            } else if (appt.date.isNotBlank() && appt.startTime.isNotBlank()) {
                DateTimeZoneUtils.localToUtcIso(appt.date, appt.startTime, -180)
            } else null

            val endUtc = if (appt.endUtcIso.isNotBlank()) {
                appt.endUtcIso
            } else if (appt.date.isNotBlank() && appt.endTime.isNotBlank()) {
                DateTimeZoneUtils.localToUtcIso(appt.date, appt.endTime, -180)
            } else null

            if (startUtc != null && endUtc != null) Pair(startUtc, endUtc) else null
        }

        val normSelectedDate = normalizeDateIso(selectedDateStr)
        val appointmentsOnDate = _uiState.value.existingAppointments.filter { appt ->
            val status = appt.status.trim().uppercase()
            val isTaken = status != "CANCELLED" && status != "CANCELED"
            val apptDateNorm = normalizeDateIso(appt.date)
            val matchesDate = apptDateNorm == normSelectedDate || (apptDateNorm.isBlank() && isToday)
            isTaken && matchesDate
        }

        val slots = mutableListOf<String>()
        val slotInterval = 30 // Slots generated every 30 minutes

        for (interval in intervals) {
            val startMin = parseTimeToMinutes(interval.startTime, -1)
            val endMin = parseTimeToMinutes(interval.endTime, -1)
            if (startMin < 0 || endMin <= startMin) continue

            var currentMinutes = startMin
            while (currentMinutes + step <= endMin) {
                val slotEnd = currentMinutes + step

                // Skip past slots for today
                val isPast = isToday && currentMinutes <= nowThresholdMinutes

                val startH = (currentMinutes / 60).toString().padStart(2, '0')
                val startM = (currentMinutes % 60).toString().padStart(2, '0')
                val slotStartStr = "$startH:$startM"

                val endH = (slotEnd / 60).toString().padStart(2, '0')
                val endM = (slotEnd % 60).toString().padStart(2, '0')
                val slotEndStr = "$endH:$endM"

                val slotStartUtc = DateTimeZoneUtils.localToUtcIso(selectedDateStr, slotStartStr, currentDt.utcOffsetMinutes)
                val slotEndUtc = DateTimeZoneUtils.localToUtcIso(selectedDateStr, slotEndStr, currentDt.utcOffsetMinutes)

                // Overlap with existing booked appointments in UTC
                val overlapsExistingUtc = activeAppointmentsWithUtc.any { (apptStartUtc, apptEndUtc) ->
                    slotStartUtc < apptEndUtc && slotEndUtc > apptStartUtc
                }

                // Fallback: Overlap with local date appointments
                val overlapsExistingLocal = appointmentsOnDate.any { appt ->
                    val apptStart = parseTimeToMinutes(appt.startTime, -1)
                    val rawEnd = parseTimeToMinutes(appt.endTime, -1)
                    val apptEnd = if (rawEnd > apptStart) rawEnd else if (apptStart >= 0) (apptStart + 30) else -1
                    if (apptStart >= 0 && apptEnd > apptStart) {
                        !(slotEnd <= apptStart || currentMinutes >= apptEnd)
                    } else {
                        false
                    }
                }

                val overlapsExisting = overlapsExistingUtc || overlapsExistingLocal

                if (!isPast && !overlapsExisting && !slots.contains(slotStartStr)) {
                    slots.add(slotStartStr)
                }

                currentMinutes += slotInterval
            }
        }

        return slots
    }

    private fun computeEndTime(start: String, durationMinutes: Int): String {
        return try {
            val parts = start.split(":")
            val hour = parts[0].toInt()
            val min = parts[1].toInt()
            val totalMin = hour * 60 + min + durationMinutes
            val endHour = (totalMin / 60) % 24
            val endMin = totalMin % 60
            val hStr = endHour.toString().padStart(2, '0')
            val mStr = endMin.toString().padStart(2, '0')
            "$hStr:$mStr"
        } catch (_: Throwable) {
            "10:00"
        }
    }
}
